// Цифровой UGC-артикул на устройстве (ТЗ 31.10.2026, п. 4.1).
//
// Покупатель вводит в поиск 8 цифр из поста или видео, витрина открывает
// карточку товара и 30 дней помнит артикул - так же, как партнёрскую ссылку
// ?promo= (referral-promo.ts). Дальше код уходит в оформление заказа полем
// `ugcArticleCode`: шлюз засчитывает заказ артикулу и начисляет партнёру
// процент. Последний введённый артикул заменяет прежний (last touch) - так
// договорено в контракте: «последний артикул из памяти устройства».
//
// Хранилище - localStorage, каждое обращение в try/catch: приватный режим
// или запрет сайтовых данных не должны ронять поиск и оформление.
import {
	UGC_ATTRIBUTION_DAYS,
	UgcStoredAttributionSchema,
	type UgcStoredAttribution,
} from "@osmix/contracts/ugc-articles";

const STORAGE_KEY = "osmix-ugc-article";
const TTL_MS = UGC_ATTRIBUTION_DAYS * 24 * 60 * 60 * 1000;

const write = (value: UgcStoredAttribution): void => {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
	} catch {
		// Хранилище закрыто - заказ просто не получит артикул с устройства,
		// шлюз поищет переход по устройству сам.
	}
};

const remove = (): void => {
	try {
		window.localStorage.removeItem(STORAGE_KEY);
	} catch {
		// нечего удалять
	}
};

/** Непротухший артикул с устройства или null. */
export const readUgcAttribution = (now = Date.now()): UgcStoredAttribution | null => {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = UgcStoredAttributionSchema.safeParse(JSON.parse(raw));
		if (!parsed.success) {
			remove();
			return null;
		}
		const value = parsed.data as UgcStoredAttribution;
		const expires = Date.parse(value.expiresAt);
		if (!Number.isFinite(expires) || expires <= now) {
			remove();
			return null;
		}
		return value;
	} catch {
		return null;
	}
};

/** Код для поля `ugcArticleCode` оформления заказа. */
export const readUgcArticleCode = (): string | undefined => readUgcAttribution()?.code;

/** Запомнить артикул после резолва. Срок - 30 дней от ввода кода. */
export const saveUgcAttribution = (
	value: Pick<UgcStoredAttribution, "code" | "articleId" | "productId" | "shopId" | "partnerId">,
	now = Date.now(),
): UgcStoredAttribution | null => {
	if (typeof window === "undefined") return null;
	const stored: UgcStoredAttribution = {
		...value,
		savedAt: new Date(now).toISOString(),
		expiresAt: new Date(now + TTL_MS).toISOString(),
	};
	write(stored);
	return stored;
};

/** Шлюз вернул свой срок перехода - храним его, а не свой расчёт. */
export const extendUgcAttribution = (code: string, expiresAt: string | null | undefined): void => {
	if (!expiresAt || !Number.isFinite(Date.parse(expiresAt))) return;
	const current = readUgcAttribution();
	if (current?.code === code) write({ ...current, expiresAt });
};
